import { IColor, IVector3, LineType } from '../api.js';
// Compare if two bounding-boxes are equal. Does not use an epsilon because we use it to
// detect redundant updates with the same bounds.
function bounds_equal(a, b) {
    return (a[0].x === b[0].x &&
        a[0].y === b[0].y &&
        a[0].z === b[0].z &&
        a[1].x === b[1].x &&
        a[1].y === b[1].y &&
        a[1].z === b[1].z);
}
function color_equal(a, b) {
    return a.r === b.r && a.g === b.g && a.b === b.b;
}
/**
 * LineMeasureWidget
 * Construct a line updatable meausurment line.
 * The line have two endcaps.
 */
export class LineMeasureWidget {
    /**
     * All parameters except for viewer and line mode can be updated after creations.
     * @param _viewer - The viewer instance.
     * @param start - Start point of the line in 3D
     * @param end - End point of the line in 3D
     * @param color - The Color of the measurment line
     * @param thickness - How thick the line is in meters
     * @param cap_axis - In which direction should the end_caps point.
     * @param cap_len - How long are the end caps
     * @param _line_type - Line mode. SOLID by default. Can also be set to DASHED
     * @param _draw_on_top - If the line should be drawn on top of other objects
     *
     */
    constructor(_viewer, start, end, color, thickness, cap_axis, cap_len, _line_type = LineType.SOLID, _draw_on_top = false, view) {
        this._viewer = _viewer;
        this.start = start;
        this.end = end;
        this.color = color;
        this.thickness = thickness;
        this.cap_axis = cap_axis;
        this.cap_len = cap_len;
        this._line_type = _line_type;
        this._draw_on_top = _draw_on_top;
        this._line = -1;
        this._capa = -1;
        this._capb = -1;
        this._visible = true;
        this._viewer = _viewer;
        const advanced_viewer = this._viewer;
        this._view = view !== null && view !== void 0 ? view : advanced_viewer.get_default_view();
        this._cap = new IVector3(cap_axis.x * cap_len, cap_axis.y * cap_len, cap_axis.z * cap_len);
        this.create_lines(); // will set private members: line, capa, capb
    }
    /**
     * @returns canvas position if line mid point is on screen, null otherwise.
     */
    get_2d_midpoint() {
        const center = this.get_center();
        return this._view.project_world_position_to_canvas(center);
    }
    /**
     * @returns line's center point in 3D
     */
    get_center() {
        return {
            x: (this.start.x + this.end.x) * 0.5,
            y: (this.start.y + this.end.y) * 0.5,
            z: (this.start.z + this.end.z) * 0.5
        };
    }
    /**
     * Check if a line is occluded.
     * @param occluders which objects are considered as occluders, Defaults to all objects in scene
     * @returns true if an object occludes the midpoint of the line.
     */
    is_occluded(occluders) {
        const engine_camera = this._view.camera_manager.engine_camera;
        occluders = occluders || this._viewer.get_all_objects_from_mask(this._view.get_render_layer_mask());
        const camera_position = engine_camera.position;
        const line_center = this.get_center();
        const line_to_camera = {
            x: camera_position.x - line_center.x,
            y: camera_position.y - line_center.y,
            z: camera_position.z - line_center.z
        };
        const length_to_camera = Math.sqrt(Math.pow(line_to_camera.x, 2.0) +
            Math.pow(line_to_camera.y, 2.0) +
            Math.pow(line_to_camera.z, 2.0));
        // Raycast from line center towards camera, account for near plane in ray length.
        const hit = this._viewer.raycast_object_using_ray(line_center, line_to_camera, length_to_camera, occluders);
        return !!hit;
    }
    /**
     * @returns The length between the start and the end point.
     */
    length() {
        return Math.sqrt(Math.pow(this.start.x - this.end.x, 2.0) +
            Math.pow(this.start.y - this.end.y, 2.0) +
            Math.pow(this.start.z - this.end.z, 2.0));
    }
    set visible(v) {
        if (this._visible === v)
            return;
        this._visible = v;
        if (this._viewer.ui_line_exists(this._line)) {
            this._viewer.set_ui_line_visible(this._line, this._visible);
        }
        if (this._viewer.ui_line_exists(this._capa)) {
            this._viewer.set_ui_line_visible(this._capa, this._visible);
        }
        if (this._viewer.ui_line_exists(this._capb)) {
            this._viewer.set_ui_line_visible(this._capb, this._visible);
        }
        this._visible && this.update(); // Since we don't update lines when they are invisible we need to update here.
    }
    get visible() {
        return this._visible;
    }
    /**
     * This applies all the changes to the line.
     */
    update() {
        if (!this._visible)
            return; // Lines is not visible
        this._cap = new IVector3(this.cap_axis.x * this.cap_len, this.cap_axis.y * this.cap_len, this.cap_axis.z * this.cap_len);
        this._viewer.update_ui_line(this._line, this.start, this.end, this.color, this.thickness);
        this._viewer.update_ui_line(this._capa, new IVector3(this.start.x - this._cap.x, this.start.y - this._cap.y, this.start.z - this._cap.z), new IVector3(this.start.x + this._cap.x, this.start.y + this._cap.y, this.start.z + this._cap.z), this.color, this.thickness);
        this._viewer.update_ui_line(this._capb, new IVector3(this.end.x - this._cap.x, this.end.y - this._cap.y, this.end.z - this._cap.z), new IVector3(this.end.x + this._cap.x, this.end.y + this._cap.y, this.end.z + this._cap.z), this.color, this.thickness);
    }
    dispose() {
        if (this._viewer.ui_line_exists(this._line)) {
            this._viewer.delete_ui_line(this._line);
        }
        if (this._viewer.ui_line_exists(this._capa)) {
            this._viewer.delete_ui_line(this._capa);
        }
        if (this._viewer.ui_line_exists(this._capb)) {
            this._viewer.delete_ui_line(this._capb);
        }
        this._line = -1;
        this._capa = -1;
        this._capb = -1;
    }
    create_lines() {
        this._line = this._viewer.create_ui_line(this._line_type, this.start, this.end, this.color, this.thickness, this._draw_on_top);
        this._capa = this._viewer.create_ui_line(LineType.SOLID, new IVector3(this.start.x - this._cap.x, this.start.y - this._cap.y, this.start.z - this._cap.z), new IVector3(this.start.x + this._cap.x, this.start.y + this._cap.y, this.start.z + this._cap.z), this.color, this.thickness, this._draw_on_top);
        this._capb = this._viewer.create_ui_line(LineType.SOLID, new IVector3(this.end.x - this._cap.x, this.end.y - this._cap.y, this.end.z - this._cap.z), new IVector3(this.end.x + this._cap.x, this.end.y + this._cap.y, this.end.z + this._cap.z), this.color, this.thickness, this._draw_on_top);
    }
}
export class BoxMeasurementWidget {
    constructor(viewer, meter_scale = 1.0, line_type = LineType.SOLID, view) {
        this.meter_scale = meter_scale;
        this.line_type = line_type;
        this._thickness = 0.01;
        this._cap_length = 0.03;
        this._color = new IColor(0, 0, 0);
        this._change_handlers = [];
        this._offset = 0.05;
        this._viewer = viewer;
        const advanced_viewer = this._viewer;
        this._view = view !== null && view !== void 0 ? view : advanced_viewer.get_default_view();
        this._camera_updated_observer = this._view.camera_updated_observable.add(() => {
            if (this._bounds) {
                this.update();
            }
        });
        this._wline = new LineMeasureWidget(viewer, new IVector3(0, 0, 0), new IVector3(0, 0, 0), this._color, this._thickness, new IVector3(0, 1, 0), this._cap_length, this.line_type);
        this._hline = new LineMeasureWidget(viewer, new IVector3(0, 0, 0), new IVector3(0, 0, 0), this._color, this._thickness, new IVector3(1, 0, 0), this._cap_length, this.line_type);
        this._dline = new LineMeasureWidget(viewer, new IVector3(0, 0, 0), new IVector3(0, 0, 0), this._color, this._thickness, new IVector3(1, 0, 0), this._cap_length, this.line_type);
        this._lines = [this._wline, this._hline, this._dline];
    }
    set_thickness(thickness) {
        if (thickness === this._thickness)
            return;
        this._thickness = thickness;
        this._lines.forEach((line) => {
            line.thickness = thickness;
            line.update();
        });
        this._change_handlers.forEach((handler) => {
            handler();
        });
    }
    set_cap_length(length) {
        if (length === this._cap_length)
            return;
        this._cap_length = length;
        this._lines.forEach((line) => {
            line.cap_len = this._cap_length;
            line.update();
        });
        this._change_handlers.forEach((handler) => {
            handler();
        });
    }
    set_color(color) {
        if (color_equal(this._color, color))
            return;
        this._color = color;
        this._lines.forEach((line) => {
            line.color = this._color;
            line.update();
        });
        this._change_handlers.forEach((handler) => {
            handler();
        });
    }
    add_change_handler(handler) {
        this._change_handlers.push(handler);
    }
    remove_change_handler(handler) {
        this._change_handlers = this._change_handlers.filter((value) => value !== handler);
    }
    set_offset(offset) {
        if (offset === this._offset)
            return;
        this._offset = offset;
        this.update();
    }
    set_bounds(bounds) {
        if (this._bounds && bounds_equal(this._bounds, bounds))
            return;
        this._bounds = bounds;
        this.update();
    }
    update() {
        const cam_position = this._view.camera_manager.engine_camera.position;
        if (!this._bounds || !cam_position)
            return;
        const bounds = this._bounds;
        const bmin = new IVector3(bounds[0].x * this.meter_scale, bounds[0].y * this.meter_scale, bounds[0].z * this.meter_scale);
        const bmax = new IVector3(bounds[1].x * this.meter_scale, bounds[1].y * this.meter_scale, bounds[1].z * this.meter_scale);
        const mid = new IVector3(0.5 * (bmin.x + bmax.x), 0.5 * (bmin.y + bmax.y), 0.5 * (bmin.z + bmax.z));
        const cam_offset = new IVector3(mid.x - cam_position.x, mid.y - cam_position.y, mid.z - cam_position.z);
        const xnear = cam_offset.x >= 0 ? bmin.x - this._offset : bmax.x + this._offset;
        const xfar = cam_offset.x >= 0 ? bmax.x + this._offset : bmin.x - this._offset;
        const znear = cam_offset.z >= 0 ? bmin.z : bmax.z;
        this._wline.visible = true;
        this._wline.start = new IVector3(bmin.x, bmax.y + this._offset, znear);
        this._wline.end = new IVector3(bmax.x, bmax.y + this._offset, znear);
        this._wline.update();
        this._hline.visible = true;
        this._hline.start = new IVector3(xfar, bmin.y, znear);
        this._hline.end = new IVector3(xfar, bmax.y, znear);
        this._hline.update();
        this._dline.visible = true;
        this._dline.start = new IVector3(xnear, bmin.y, bmin.z);
        this._dline.end = new IVector3(xnear, bmin.y, bmax.z);
        this._dline.update();
        this._change_handlers.forEach((handler) => {
            handler();
        });
    }
    clear() {
        this._bounds = undefined;
        this._lines.forEach((line) => {
            line.visible = false;
        });
    }
    get_line_canvas_centers() {
        return [
            this._wline.get_2d_midpoint(),
            this._hline.get_2d_midpoint(),
            this._dline.get_2d_midpoint()
        ];
    }
    test_line_deleter() {
        this._dline.start = new IVector3(0.0, -4.0, 0.0);
        this._dline.end = new IVector3(0.0, 4.0, 0.0);
        this._dline.update();
    }
    dispose() {
        this._camera_updated_observer.dispose();
    }
}
//# sourceMappingURL=MeasurementWidget.js.map